import React, { useCallback, useState } from 'react';
import { CustomExportRunItemResponse, CustomExportRunStatus } from '@ydistri/api-sdk';
import { ActionsBar, Button, GenericConfirmationModal } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { useTemplateOrCalculation } from '../../../../hooks/useTemplateOrCalculation';
import { useAppDispatch } from '../../../../store';
import { useUpdateCustomExportRunMutation } from '../apiCalculationExport';
import { shouldShowDoneWithFailedAction } from '../customExportLib';
import { useGetLatestCustomExportRun } from '../useGetLatestCustomExportRun';

interface CustomExportRunDoneWithFailedActionProps {
  customExportRunId: number;
  items: CustomExportRunItemResponse[];
}

const CustomExportRunDoneWithFailedAction: React.FC<CustomExportRunDoneWithFailedActionProps> = ({
  customExportRunId,
  items,
}) => {
  const dispatch = useAppDispatch();
  const templateOrCalculation = useTemplateOrCalculation();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [updateCustomExportRun, updateCustomExportRunStatus] = useUpdateCustomExportRunMutation();
  const { data: latestCustomExportRun } = useGetLatestCustomExportRun(templateOrCalculation);

  const customExportRunStatus = latestCustomExportRun?.status;
  const isDoneWithFailedSet = customExportRunStatus === CustomExportRunStatus.DoneWithFailed;

  const shouldShow = !isDoneWithFailedSet && shouldShowDoneWithFailedAction(items);

  const openConfirmation = useCallback(() => {
    setIsConfirmationOpen(true);
  }, []);

  const closeConfirmation = useCallback(() => {
    setIsConfirmationOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    void updateCustomExportRun({
      calculationId: templateOrCalculation.id,
      customExportRunId,
      data: {
        status: CustomExportRunStatus.DoneWithFailed,
      },
    })
      .unwrap()
      .catch(() => {
        dispatch(
          addToast({
            message: 'Setting custom export run as "Done with Failed" failed.',
            isError: true,
          }),
        );
      })
      .finally(closeConfirmation);
  }, [
    closeConfirmation,
    dispatch,
    customExportRunId,
    updateCustomExportRun,
    templateOrCalculation.id,
  ]);

  if (!shouldShow) return null;

  return (
    <>
      <ActionsBar moveToRight={true}>
        <Button
          $colorVariant="ghostPrimary"
          loading={updateCustomExportRunStatus.isLoading}
          disabled={updateCustomExportRunStatus.isLoading}
          onClick={openConfirmation}
        >
          Set as &quot;Done with Failed&quot;
        </Button>
      </ActionsBar>

      {isConfirmationOpen && (
        <GenericConfirmationModal
          title="Set as Done with Failed"
          message='Set the latest custom export run as "Done with Failed"?'
          confirmationButtonLabel="Set as Done with Failed"
          onConfirmed={handleConfirm}
          onCanceled={closeConfirmation}
          running={updateCustomExportRunStatus.isLoading}
        />
      )}
    </>
  );
};

export default CustomExportRunDoneWithFailedAction;
